import React, { forwardRef } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { SectionCard, RangeBtn, ChartTooltip, RANGES, C, axisTick } from "../shared";

const ChartSection = forwardRef(({ code, chartData, chartLoading, activeRange, setActiveRange }, ref) => {
  const closes = chartData.map(c => c.close).filter(Boolean);
  const minClose = closes.length ? Math.min(...closes) * 0.995 : "auto";
  const maxClose = closes.length ? Math.max(...closes) * 1.005 : "auto";

  return (
    <SectionCard
      ref={ref} id="chart" icon="chart" title={`Price Chart — ${code}`}
      right={
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {RANGES.map(r => (
            <RangeBtn key={r.label} label={r.label} active={activeRange.label === r.label} onClick={() => setActiveRange(r)} />
          ))}
        </div>
      }
    >
      {chartLoading ? (
        <div className="tdash-muted">Loading chart…</div>
      ) : chartData.length === 0 ? (
        <div className="tdash-muted">No chart data for this range</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="tdashPriceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={C.orange} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={C.orange} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
              <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={{ stroke: C.border }} interval="preserveStartEnd" minTickGap={40} />
              <YAxis domain={[minClose, maxClose]} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(0)} width={46} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="close" name="Close" stroke={C.orange} strokeWidth={2} fill="url(#tdashPriceGrad)" dot={false} activeDot={{ r: 4, fill: C.orange }} isAnimationActive={false} />
              <Area type="monotone" dataKey="ldcp" name="Prev Close" stroke="#3a4150" strokeWidth={1} strokeDasharray="4 4" fill="none" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 8 }}>
            <ResponsiveContainer width="100%" height={70}>
              <BarChart data={chartData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip content={({ active, payload }) =>
                  active && payload?.length
                    ? <div style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 8px", fontSize: 11, color: C.body }}>
                        Vol: <strong>{payload[0]?.value?.toLocaleString()}</strong>
                      </div>
                    : null
                } />
                <Bar dataKey="volume" fill={C.orange} opacity={0.28} radius={[2, 2, 0, 0]} maxBarSize={6} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ textAlign: "center", fontSize: 10, color: C.faint }}>Volume</div>
          </div>
        </>
      )}
    </SectionCard>
  );
});

export default ChartSection;
